import { Injectable } from '@angular/core';
import { LenderUser } from 'app/models/dto/lender-user.model';
import { PusherService } from 'app/modules/shared/services/pusher.service';
import { environment } from 'environments/environment';
import { Subject } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { notifications } from '../notification/notifications';
import { filter, map } from 'rxjs/operators';
import { notNull } from 'app/modules/shared/functions/filters';
import { Store } from '@ngxs/store';
import { Notification } from '../notification/notification.model';
import { MutateCallState, PushCallAlert } from 'app/store/call/call.actions';
import { CallState } from 'app/store/call/call.state';
import { LoadNeedsDisposition } from 'app/store/disposition/disposition.actions';
import { PhonePipe } from 'app/pipes/phone/phone.pipe';
import { DismissNotificationsForCallId } from 'app/store/notification/notification.actions';
import { cloneDeep } from 'lodash-es';

@Injectable({
  providedIn: 'root'
})
export class CallNotificationService {

  privateLenderUserChannelName: string;
  lenderUser: LenderUser;
  private callNotificationSubject = new Subject<Notification | null>();

  constructor(
    private pusherService: PusherService,
    private store: Store,
    private phonePipe: PhonePipe,
  ) {}

  subscribeToCallNotifications(lenderUser: LenderUser) {
    this.lenderUser = lenderUser;
    this.pusherService
      .subscribeRaw(`private-lender-user-${this.lenderUser.user.id}-${environment.environment}-notification-channel`)
      .bind_global((event: string, data: any) => this.callNotificationSubject.next(this.handlePrivateLenderUserCallEvent(event, data)));
  }

  unsubscribeFromCallNotifications() {
    this.pusherService.unsubscribeRaw(`private-lender-user-${this.lenderUser.user.id}-${environment.environment}-notification-channel`);
  }

  get callNotifications$() {
    return this.callNotificationSubject.asObservable().pipe(filter(notNull), map(notification => notification));
  }

  private handlePrivateLenderUserCallEvent(event: string, data: any): Notification | null {
    if (event === 'phone') {
      if (data.meta.borrowerLenderUserId === this.lenderUser.user.id || data.callQueue || data.meta.requestState) {
        switch(data.meta.type) {
          case 'callback':
          case 'callInitiated':
            setTimeout(() => {
              this.store.dispatch(new MutateCallState('connected', data.meta));
            }, 2000);
          break;
          case 'callLeft':
          case 'callMerged':
          case 'muteState':
          case 'onHold':
            this.store.dispatch(new MutateCallState('', data.meta));
          break;
          case 'internalCallDismissed':
            this.store.dispatch(new PushCallAlert('info', 'The internal call you made was dismissed by the user.'));
          case 'callended':
          case 'callCompleted':
            this.store.dispatch(new MutateCallState('disconnected', data.meta));
            this.store.dispatch(new DismissNotificationsForCallId(data.meta.callId));
          break;
          case 'callclaimed':
          case 'callDismissed':
          case 'callAnswered':
          case 'queueLeave':
            this.store.dispatch(new DismissNotificationsForCallId(data.meta.callId));
          break;
          case 'internalCallAnswered':
          case 'unHold':
            this.store.dispatch(new MutateCallState('connected', data.meta));
          break;
          case 'incoming':
            return this.handleIncomingCallEvent(event, data);
          case 'callJoined':
            this.store.dispatch(new MutateCallState('call joined', data.meta));
          break;
          case 'dispositionRequired':
            this.handleDispositionRequired(data.meta);
          break;
        }
      }
    }
    return null;
  }

  private handleDispositionRequired(meta: any): void {
    const activeCall = this.store.selectSnapshot(CallState.call);
    const openCallId = meta?.callId;
    const staleActiveCall = openCallId != null
      && activeCall?.callId != null
      && Number(activeCall.callId) === Number(openCallId)
      && activeCall.callState !== 'disconnected';

    if (staleActiveCall) {
      this.store.dispatch(new MutateCallState('disconnected', { callId: Number(openCallId), callStatus: 'completed' }));
    }
    this.store.dispatch(new LoadNeedsDisposition(true));
  }

  private handleIncomingCallEvent(event: string, { title, borrowerId, subtitle, meta, callQueue }) {
    let notification = callQueue && meta.roundRobin
      ? notifications.find(n => n.type === 'callQueue') as Notification
      : notifications.find(n => n.type === event) as Notification;
    if (notification) {
      notification = cloneDeep(notification);
      notification.title = title;
      notification.borrowerId = borrowerId;
      if (callQueue) {
        notification.content = `${callQueue.name} - ${this.phonePipe.transform(meta.phone)}`;
      } else {
        notification.content = subtitle ? `${subtitle} - ${this.phonePipe.transform(meta.phone)}` : this.phonePipe.transform(meta.phone);
      }
      notification.id = uuidv4();
      notification.created = new Date();
      notification.meta = {
        callQueue: callQueue,
        callId: meta.callId,
        connectSoftPhone: meta.connectSoftPhone,
      };
      return notification;
    }
    return null;
  }
}
